import { useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ArrowUpRight, ClipboardList } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { listEvents } from '../../api/events'
import { PageHeader } from '../../components/layout/PageHeader'
import { EmptyState, StatusBadge } from '../../components/common/Status'
import { TimeAgo } from '../../components/common/TimeAgo'
import { Skeleton } from '../../components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { Button } from '../../components/ui/button'
import { eventTypeLabel, eventStatusLabel } from '../../lib/alert-level'
import { formatCoord, formatMag } from '../../lib/format'
import type { Event } from '../../api/schemas'
import { cn } from '../../lib/utils'
import { useVirtualTable } from '../../hooks/useVirtualTable'
import { VirtualRow } from '../../components/common/VirtualRow'
import { EventDetailDialog } from './components/EventDetailDialog'

const STATUS_TABS = [
  { key: 'all', label: '全部' },
  { key: 'pending', label: '确认中' },
  { key: 'confirmed', label: '已确认' },
  { key: 'closed', label: '已关闭' },
]

const PAGE_SIZE = 100
const ROW_HEIGHT = 48

export function EventsPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState('all')
  const [selected, setSelected] = useState<Event | null>(null)

  const query = useInfiniteQuery({
    queryKey: ['events', 'list', { status, source: 'events-page' }],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => listEvents({ status, page: pageParam, pageSize: PAGE_SIZE }),
    getNextPageParam: (last, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.items.length, 0)
      return loaded < last.total ? allPages.length + 1 : undefined
    },
    staleTime: 15_000,
    retry: 1,
  })

  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data])
  const total = query.data?.pages[0]?.total ?? 0
  const { containerRef, virtualizer } = useVirtualTable({ count: rows.length, estimateSize: ROW_HEIGHT })

  // 滚动到底加载下一页
  const virtualItems = virtualizer.getVirtualItems()
  const lastItem = virtualItems[virtualItems.length - 1]
  if (lastItem && !query.isFetchingNextPage && query.hasNextPage && lastItem.index >= rows.length - 20) {
    void query.fetchNextPage()
  }

  return (
    <>
      <PageHeader
        breadcrumb="事件工作台"
        title="告警流水"
        description={`聚合事件与地震候选 · 共 ${total} 条 · 回溯查询`}
      />

      <div className="panel-glass overflow-hidden rounded-xl">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <Tabs value={status} onValueChange={setStatus}>
            <TabsList>
              {STATUS_TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <span className="text-[10px] text-muted-foreground">
            {query.isFetching ? '同步中…' : `已加载 ${rows.length} / ${total}`}
          </span>
        </div>

        {query.isPending ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : query.isError ? (
          <EmptyState
            title="数据加载失败"
            description={query.error.message}
            icon={<ClipboardList size={28} />}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="暂无事件"
            description="后端确认或聚合出事件后将在此展示"
            icon={<ClipboardList size={28} />}
          />
        ) : (
          <div>
            <div className="flex items-center gap-3 border-b border-border px-4 py-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              <span className="w-20 shrink-0">时间</span>
              <span className="w-16 shrink-0">类型</span>
              <span className="min-w-0 flex-1">事件</span>
              <span className="w-16 shrink-0 text-right">震级</span>
              <span className="w-20 shrink-0">位置</span>
              <span className="w-20 shrink-0 text-right">触发</span>
              <span className="w-16 shrink-0">状态</span>
            </div>
            <div ref={containerRef} className="relative max-h-[calc(100vh-360px)] overflow-auto">
              <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
                {virtualItems.map((vItem) => {
                  const event = rows[vItem.index]
                  if (!event) return null
                  return (
                    <VirtualRow
                      key={event.eventId}
                      start={vItem.start}
                      height={vItem.size}
                      className="px-4"
                    >
                      <button
                        onClick={() => setSelected(event)}
                        className={cn(
                          'flex h-full w-full items-center gap-3 border-b border-border/60 text-left text-xs transition hover:bg-foreground/[.03]',
                          selected?.eventId === event.eventId && 'bg-primary/[.06]',
                        )}
                      >
                        <span className="w-20 shrink-0">
                          <TimeAgo ts={event.originTime} className="block text-muted-foreground" />
                        </span>
                        <span className="w-16 shrink-0 text-muted-foreground">{eventTypeLabel(event.type)}</span>
                        <span className="min-w-0 flex-1 truncate text-foreground/90">
                          {event.name || event.eventId}
                          <span className="ml-2 font-mono text-[10px] text-muted-foreground/50">{event.eventId}</span>
                        </span>
                        <span className="w-16 shrink-0 text-right font-mono font-semibold text-critical">
                          {formatMag(event.magnitude)}
                        </span>
                        <span className="w-20 shrink-0 truncate font-mono text-[10px] text-muted-foreground">
                          {formatCoord(event.lat, event.lon)}
                        </span>
                        <span className="w-20 shrink-0 text-right font-mono tabular-nums text-muted-foreground">
                          {event.confirmedDevices}/{event.triggerCount}
                        </span>
                        <span className="w-16 shrink-0">
                          <StatusBadge status={event.status} labels={eventStatusLabel} />
                        </span>
                      </button>
                    </VirtualRow>
                  )
                })}
              </div>
              {query.isFetchingNextPage && (
                <div className="py-2 text-center text-[10px] text-muted-foreground">加载更多…</div>
              )}
            </div>
          </div>
        )}
      </div>

      <EventDetailDialog event={selected} onClose={() => setSelected(null)}>
        {selected && (
          <Button variant="outline" size="sm" onClick={() => void navigate(`/events/${selected.eventId}`)}>
            完整时间线 <ArrowUpRight size={13} />
          </Button>
        )}
      </EventDetailDialog>
    </>
  )
}

